<script setup lang="ts">
import { LocalizedString } from '@commercetools/platform-sdk';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import PopperWrapper from '@/components/base/layout/PopperWrapper.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { useCart } from '@/composables/useCart';
import { useDelivery } from '@/composables/useDelivery';
import { useDiscount } from '@/composables/useDiscount';
import { reportError } from '@/utils/reportError';

const props = defineProps<{
  discountCode: { code?: string; label: string; message?: LocalizedString };
  pendingCodes?: boolean;
}>();

const emit = defineEmits<{
  'remove-error-message': [];
  'set-error-message': [message: string];
}>();

const loading = defineModel<boolean>('loading', { required: true });

const route = useRoute();
const router = useRouter();
const store = useStore();

const { unassignedLineItems } = useCart(store);
const { hasMultipleShipments } = useDelivery(store);
const { appliedDiscountCodes, applyDiscountCode, removeDiscountCode } = useDiscount(store);

const applied = computed(() =>
  appliedDiscountCodes.value.some(
    (appliedDiscountCode) => appliedDiscountCode.label === props.discountCode.label,
  ),
);
const actionStatus = ref<'Apply' | 'Applying' | 'Applied' | 'Remove' | 'Removed' | 'Removing'>(
  applied.value ? 'Remove' : 'Apply',
);
const isPending = computed(() => !['Apply', 'Remove'].includes(actionStatus.value));

watch(applied, (newValue) => {
  actionStatus.value = newValue ? 'Remove' : 'Apply';
});

const applyDiscount = async (discountCode: string) => {
  loading.value = true;
  actionStatus.value = 'Applying';
  emit('remove-error-message');
  try {
    await applyDiscountCode(discountCode);
    actionStatus.value = 'Applied';

    if (route.path.startsWith('/checkout')) {
      if (unassignedLineItems.value.length && hasMultipleShipments.value) {
        router.push('/checkout/address/multi');
      } else {
        router.push('/checkout/shipping');
      }
    }

    loading.value = false;
    setTimeout(() => {
      actionStatus.value = 'Remove';
    }, 3000);
  } catch (error) {
    actionStatus.value = 'Apply';
    let message = reportError(error);
    if (message === 'Bad Request') message = 'Failed to apply discount code';
    emit('set-error-message', message);
    loading.value = false;
  }
};

const removeDiscount = async (discountCode: string) => {
  loading.value = true;
  actionStatus.value = 'Removing';
  emit('remove-error-message');
  try {
    await removeDiscountCode(discountCode);
    actionStatus.value = 'Removed';

    if (route.path.startsWith('/checkout')) {
      if (unassignedLineItems.value.length && hasMultipleShipments.value) {
        router.push('/checkout/address/multi');
      } else {
        router.push('/checkout/shipping');
      }
    }

    loading.value = false;
    setTimeout(() => {
      actionStatus.value = 'Apply';
    }, 3000);
  } catch (error) {
    actionStatus.value = 'Remove';
    let message = reportError(error);
    if (message === 'Bad Request') message = 'Failed to remove discount code';
    emit('set-error-message', message);
    loading.value = false;
  }
};

const infoIconFilled = nutshell['img/info-icon-filled.svg'];
</script>

<template>
  <div>
    <div v-if="pendingCodes" class="flex justify-between gap-2">
      <div>
        {{ discountCode.label }}
      </div>
      <div class="flex">
        <div class="text-xs italic font-style:">
          <p>Pending</p>
        </div>
        <div class="inline-block tiny-note popover-container">
          <PopperWrapper arrow class="legacy-max-width" offsetDistance="10" placement="top">
            <template v-slot:content>
              <div
                v-if="discountCode.message"
                class="p-3 text-xs text-neutral-500"
                data-test="surcharge-popper"
              >
                {{ discountCode.message.en }}
              </div>
            </template>
            <img
              role="button"
              alt="pending discount info tooltip"
              class="cursor-pointer ml-2 transform -translate-y-0.5"
              :src="infoIconFilled"
            />
          </PopperWrapper>
          <span class="sr-only"> pending discount reminder details are shown in a tooltip </span>
        </div>
      </div>
    </div>
    <div v-else-if="discountCode.code" class="flex justify-between gap-4">
      <div :class="{ 'text-lime-700': applied }">
        {{ discountCode.label }}
      </div>
      <UnstyledButton
        class="text-xs text-nuts-cyan-700"
        :class="{ underline: !isPending }"
        :disabled="isPending"
        key="applied ? 'remove' : 'apply'"
        @click="applied ? removeDiscount(discountCode.code) : applyDiscount(discountCode.code)"
      >
        {{ actionStatus }}
      </UnstyledButton>
    </div>
  </div>
</template>
